export type ContractStatus = "ATIVO" | "INATIVO" | "EM_CONFIGURACAO";

export interface IContractStatusEntry {
  workspaceId: string;
  status: ContractStatus;
  author: string;
  updatedAt: string;
}

/**
 * Regra de partida (Ponto 3 do plano de melhorias de lógica, 2026-08-10):
 * classificação por corretora, levantada no Postgres de produção (só
 * leitura) em 2026-08-10. Sem coluna nenhuma no banco — é intencional
 * (usuário pediu explicitamente "só frontend, não deve afetar nada no banco
 * de dados"). Nenhum critério de INATIVO foi definido ainda; nada cai nesse
 * estado por esta regra.
 *
 * Reconferido no Postgres de produção em 2026-08-11. O que esta regra
 * classifica como ATIVO hoje: Galcorr (6 CNPJs / 10 workspaces), Sopeh
 * (4 / 4) e a Enesa sob a Alper (10 / 17) — 20 CNPJs no total.
 *
 * A comparação é com `COALESCE(trading_name, corporate_name)` da
 * organização vendedora, que é o que o backend envia como `sellerName`.
 * Os valores exatos em produção são "Galcorr" e "Sopeh" — os
 * `corporate_name` ("Galcorr Corretora de Seguros LTDA", "Sopeh
 * Consultoria e Corretagem de Seguros LTDA") NÃO chegam aqui e não devem
 * ser usados. A versão anterior desta lista dizia "Corretora Galcorr",
 * que não existe no banco: nunca casou, e os 6 CNPJs da Galcorr caíam em
 * EM_CONFIGURACAO contra a intenção da regra.
 */
const ALWAYS_ATIVO_SELLER_NAMES = new Set(["Galcorr", "Sopeh"]);

const ALPER_SELLER_NAME = "ALPER CONSULTORIA E CORRETORA DE SEGUROS SA";

/**
 * CNPJs da Enesa sob a Alper — únicos contratos da Alper que nascem ATIVO
 * pela regra de partida. São os 10 CNPJs da Enesa existentes em produção,
 * conferidos em 2026-08-11: a lista é a Enesa inteira, não um subconjunto.
 *
 * `48785828007727` faltava na versão anterior (2 workspaces criados em
 * 19/01/2026 — existiam bem antes do levantamento, foi omissão) e foi
 * confirmado como ATIVO pelo Gustavo em 2026-08-11.
 */
const ALPER_ATIVO_FEDERAL_TAX_IDS = new Set([
  "48785828000129",
  "48785828001010",
  "48785828007050",
  "48785828007727",
  "48785828008456",
  "48785828008960",
  "48785828009266",
  "48785828009347",
  "48785828009428",
  "48785828009509",
]);

/** Regra de partida — resolve o default antes de overrides versionados/de sessão entrarem. */
export function getDefaultContractStatus(sellerName: string, buyerFederalTaxId: string): ContractStatus {
  if (ALWAYS_ATIVO_SELLER_NAMES.has(sellerName)) return "ATIVO";
  if (sellerName === ALPER_SELLER_NAME && ALPER_ATIVO_FEDERAL_TAX_IDS.has(buyerFederalTaxId)) return "ATIVO";
  return "EM_CONFIGURACAO";
}

/**
 * Overrides revisados por workspace, mesmo espírito do `muted.ts` (F0.4
 * caminho B): editar via PR, dá revisão e histórico à mudança. Só precisa de
 * entrada aqui quando o estado divergir da regra de partida acima (ex.: um
 * contrato específico virou INATIVO, ou algo fora da regra de seller virou
 * ATIVO manualmente pela tela de Cadastro).
 *
 * Exemplo:
 * { workspaceId: "ws-...", status: "INATIVO", author: "gustavo", updatedAt: "2026-08-10T00:00:00-03:00" },
 */
export const CONTRACT_STATUS_OVERRIDES: IContractStatusEntry[] = [];
